import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, Search, ShoppingBag, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { NAV } from "@/content/nav";
import { useCart, useSite } from "@/context/Providers";
import { SearchPanel } from "@/components/SearchPanel";

const Dropdown = ({ item }) => {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  return (
    <li className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
      onKeyDown={(e) => e.key === "Escape" && setOpen(false)}>
      <button type="button" aria-expanded={open} aria-haspopup="true" onClick={() => setOpen((v) => !v)}
        className="flex min-h-[44px] items-center gap-1 px-3 text-sm font-semibold text-gray-200 hover:text-gold"
        data-testid={`nav-${item.label.toLowerCase()}-menu-button`}>
        {item.label} <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
      {open && (
        <ul className="absolute left-0 top-full z-50 w-60 rounded-2xl border border-line bg-panel p-2 shadow-2xl">
          {item.children.map((c) => (
            <li key={c.to}>
              <Link to={c.to} className="block rounded-xl px-4 py-3 text-sm text-gray-300 hover:bg-raised hover:text-gold"
                data-testid={`nav-link-${c.to.replace(/\//g, "-").replace(/^-|-$/g, "")}`}>{c.label}</Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const Logo = () => (
  <Link to="/" className="flex items-center gap-3" aria-label="SHADC Dispensary home" data-testid="header-logo-link">
    <img src="/brand/shadc-logo-96.webp" alt="" width="48" height="48" className="h-11 w-11 drop-shadow-[0_0_10px_rgba(16,185,129,0.35)] sm:h-12 sm:w-12" />
    <span className="leading-none">
      <span className="block font-display text-xl font-bold tracking-wide text-white">SHA<span className="text-gold">DC</span></span>
      <span className="mt-1 block text-[10px] font-semibold uppercase tracking-[0.28em] text-gray-400">Dispensary</span>
    </span>
  </Link>
);

const MobileMenu = ({ open, setOpen }) => (
  <Sheet open={open} onOpenChange={setOpen}>
    <SheetContent side="left" className="w-[86vw] max-w-sm overflow-y-auto border-line bg-ink p-6 text-gray-100" data-testid="mobile-nav">
      <SheetTitle className="sr-only">Site navigation</SheetTitle>
      <nav aria-label="Mobile" className="mt-6 space-y-6">
        {NAV.map((n) => (
          <div key={n.label}>
            <Link to={n.to} onClick={() => setOpen(false)} className="font-display text-2xl text-white">{n.label}</Link>
            {n.children && (
              <ul className="mt-2 grid grid-cols-2 gap-1">
                {n.children.map((c) => (
                  <li key={c.to}><Link to={c.to} onClick={() => setOpen(false)} className="block py-2 text-base text-gray-400 hover:text-gold">{c.label}</Link></li>
                ))}
              </ul>
            )}
          </div>
        ))}
        <Link to="/shop/" onClick={() => setOpen(false)} className="btn-gold w-full" data-testid="mobile-nav-order-button">Order Now</Link>
      </nav>
    </SheetContent>
  </Sheet>
);

export const Header = () => {
  const cart = useCart();
  const site = useSite();
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState(false);
  const loc = useLocation();
  const searchBtn = useRef(null);
  useEffect(() => { setSearch(false); setMenu(false); }, [loc.pathname, loc.search]);

  return (
    <>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[90] focus:rounded-full focus:bg-gold focus:px-4 focus:py-2 focus:text-ink">Skip to content</a>
      <div className="border-b border-line bg-panel text-center text-xs font-medium tracking-wide text-gray-300" data-testid="announcement-bar">
        <p className="container-x py-2">
          Free delivery over ${site?.free_delivery_minimum ?? 50} <span className="mx-2 text-gold">◆</span> Adults 21+<span className="hidden sm:inline"> with valid ID</span>
          <span className="mx-2 hidden text-gold sm:inline">◆</span><span className="hidden sm:inline">Mon–Sat 8AM–8PM</span>
        </p>
      </div>
      <header className="sticky top-0 z-50 border-b border-line bg-ink/90 backdrop-blur-md">
        <div className="container-x flex h-[72px] items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setMenu(true)} className="-ml-2 grid h-11 w-11 place-items-center rounded-full text-gray-200 lg:hidden"
              aria-label="Open menu" data-testid="mobile-menu-button"><Menu className="h-6 w-6" /></button>
            <Logo />
          </div>
          <nav aria-label="Primary" className="hidden lg:block">
            <ul className="flex items-center">
              {NAV.map((n) => n.children ? <Dropdown key={n.label} item={n} /> : (
                <li key={n.label}>
                  <NavLink to={n.to} className={({ isActive }) => `flex min-h-[44px] items-center px-3 text-sm font-semibold ${isActive ? "text-gold" : "text-gray-200 hover:text-gold"}`}
                    data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}>{n.label}</NavLink>
                </li>
              ))}
            </ul>
          </nav>
          <div className="flex items-center gap-1 sm:gap-2">
            <button ref={searchBtn} type="button" onClick={() => setSearch((v) => !v)} aria-expanded={search} aria-label={search ? "Close search" : "Search products"}
              className="grid h-11 w-11 place-items-center rounded-full text-gray-200 hover:text-gold" data-testid="header-search-button">
              {search ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
            </button>
            <button type="button" onClick={() => cart.setOpen(true)} aria-label={`Open cart, ${cart.count} items`}
              className="relative grid h-11 w-11 place-items-center rounded-full text-gray-200 hover:text-gold" data-testid="header-cart-button">
              <ShoppingBag className="h-5 w-5" />
              {cart.count > 0 && <span className="absolute right-0.5 top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-gold px-1 text-[11px] font-bold text-ink" data-testid="header-cart-count">{cart.count}</span>}
            </button>
            <Link to="/shop/" className="btn-gold hidden min-h-[44px] px-5 sm:inline-flex" data-testid="header-order-now-button">Order Now</Link>
          </div>
        </div>
        {search && <SearchPanel onClose={() => { setSearch(false); searchBtn.current?.focus(); }} />}
      </header>
      <MobileMenu open={menu} setOpen={setMenu} />
    </>
  );
};
